import { type JSX, useState } from 'react';
import { isUndefined } from 'dg-web-shared/lib/MaybeV2';
import { InputContext } from 'dg-web-shared/tb-ui/inputs/InputContext.ts';
import {
    LabeledToggle,
    ToggleType,
} from 'dg-web-shared/tb-ui/toggle/LabeledToggle.tsx';
import { CurrentLoginState } from '../../../common/state/CurrentLoginState';
import { getBridge } from '../../../Native';
import { ErrorBlock } from '../../root/components/ErrorBlock';
import { Localized } from 'dg-web-shared/lib/Localization.tsx';
import { NotificationsBlock } from './NotificationsBlock';
import { useWriteEditLoginData } from './NewsletterEdit';
import { RequestStatus } from 'dg-web-shared/lib/ServerRequestStateSlices';
import { GenericErrorBody } from '../../../ui/modals/HandleHttpStati';

interface EditState {
    automaticStop: boolean;
}

export const TransactionReminderEditForm = (p: {
    currentLoginData: CurrentLoginState.CurrentLogin;
}) => {
    const initialState = {
        automaticStop: p.currentLoginData.sendRunningTransactionsAutoTerminate,
    };

    const [editState, setEditState] = useState<EditState>(initialState);

    const [expanded, setExpanded] = useState(false);

    const [httpPut, doHttpPut, resetPut] = useWriteEditLoginData(
        () => setExpanded(false),
        false,
        'transactionReminder-edit',
    );

    const isSaveable =
        editState.automaticStop !==
        p.currentLoginData.sendRunningTransactionsAutoTerminate;

    const pending = httpPut.status === RequestStatus.PENDING;

    const contentText = () => {
        if (p.currentLoginData !== null) {
            const messages: Array<JSX.Element> = [];
            if (
                p.currentLoginData.device !== null &&
                p.currentLoginData.device.fcmToken !== null &&
                !!p.currentLoginData.device.status
            ) {
                messages.push(
                    <Localized
                        de="vor dem Ablauf"
                        fr="avant expiration"
                        it="prima della scadenza"
                        en="before expiration"
                    />,
                );
            }

            if (p.currentLoginData.sendRunningTransactionsAutoTerminate) {
                messages.push(
                    <Localized
                        de="beim autom. Stopp"
                        fr="arrêtée autom."
                        it="fermata autom."
                        en="autom. stop"
                    />,
                );
            }

            if (messages.length === 0) {
                messages.push(<Localized de="—" fr="—" it="—" en="—" />);
            }

            return messages.map((x: JSX.Element, idx: number) => {
                return (
                    <span key={x.props.en}>
                        {x}
                        {idx < messages.length - 1 && ', '}
                    </span>
                );
            });
        }
    };

    return (
        <NotificationsBlock
            caption={
                <Localized
                    de="Laufende Parkvorgänge"
                    fr="Procédures en cours"
                    it="Procedure in corso"
                    en="Running procedures"
                />
            }
            contentElement={
                <div>
                    <RenderEditContent
                        currentLoginData={p.currentLoginData}
                        setEditState={setEditState}
                        editState={editState}
                    />
                    {httpPut.status === RequestStatus.ERROR && (
                        <ErrorBlock text={<GenericErrorBody />} />
                    )}
                </div>
            }
            toggle={(): void => {
                setEditState({ ...initialState });
                setExpanded(!expanded);
                resetPut();
            }}
            expanded={expanded}
            onSave={() => {
                doHttpPut({
                    ...p.currentLoginData,
                    sendRunningTransactionsAutoTerminate:
                        editState.automaticStop,
                });
            }}
            disabled={!isSaveable}
            text={contentText()}
            pending={pending}
        />
    );
};

interface RenderEditContentProps {
    currentLoginData: CurrentLoginState.CurrentLogin;
    setEditState: (edit: EditState) => void;
    editState: EditState;
}

const RenderEditContent = (props: RenderEditContentProps) => {
    if (isUndefined(props.currentLoginData)) {
        return null;
    } else {
        const device = props.currentLoginData.device;

        return (
            <>
                {device !== null && device.fcmToken !== null && (
                    <LabeledToggle
                        context={InputContext.form}
                        type={ToggleType.checkbox}
                        selected={!!device.status}
                        label={
                            <Localized
                                de={`${device.autoTerminationPreWarningMinutes} Min. vor dem Ablauf (nur Push)`}
                                fr={`${device.autoTerminationPreWarningMinutes} min. avant expiration (seulement push)`}
                                it={`${device.autoTerminationPreWarningMinutes} min. prima della scadenza (solo push)`}
                                en={`${device.autoTerminationPreWarningMinutes} min. before expiration (push only)`}
                            />
                        }
                        onClick={() => {
                            const bridge = getBridge();
                            if (bridge) {
                                bridge.notificationSettings();
                            }
                        }}
                    />
                )}

                <LabeledToggle
                    context={InputContext.form}
                    type={ToggleType.checkbox}
                    selected={props.editState.automaticStop}
                    label={
                        <Localized
                            de="Beim automatischen Stopp"
                            fr="Dans le cas où elle s'est arrêtée automatiquement"
                            it="Nel caso termini automaticamente"
                            en="In case it stops automatically"
                        />
                    }
                    onClick={() => {
                        props.setEditState({
                            ...props.editState,
                            automaticStop: !props.editState.automaticStop,
                        });
                    }}
                />
            </>
        );
    }
};
